<div class="maintenance-scheduling-container" *ngIf="isAdmin">
    <div class="page-header">
      <h1 class="page-title">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="title-icon">
          <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z" fill="#0052CC"/>
          <path d="M7 10H9V12H7V10ZM11 10H13V12H11V10ZM15 10H17V12H15V10ZM7 14H9V16H7V14ZM11 14H13V16H11V14ZM15 14H17V16H15V14Z" fill="#0052CC"/>
        </svg>
        Maintenance Scheduling
      </h1>
      <p class="page-description">Schedule and manage system maintenance windows</p>
    </div>
  
    <div class="content-grid">
      <!-- Calendar View -->
      <div class="calendar-section">
        <div class="section-header">
          <h2 class="section-title">Scheduled Maintenance Calendar</h2>
          <div class="calendar-controls">
            <button type="button" class="btn btn-ghost" (click)="previousMonth()">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <span class="current-month">{{ getCurrentMonthYear() }}</span>
            <button type="button" class="btn btn-ghost" (click)="nextMonth()">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
  
        <div class="calendar-grid">
          <div class="calendar-header">
            <div class="day-header">Sun</div>
            <div class="day-header">Mon</div>
            <div class="day-header">Tue</div>
            <div class="day-header">Wed</div>
            <div class="day-header">Thu</div>
            <div class="day-header">Fri</div>
            <div class="day-header">Sat</div>
          </div>
          
          <div class="calendar-body">
            <div class="calendar-day" 
                 *ngFor="let day of getCalendarDays()"
                 [class.other-month]="!isCurrentMonth(day)"
                 [class.today]="isToday(day)">
              <div class="day-number">{{ day.getDate() }}</div>
              <div class="day-events">
                <div class="event-dot" 
                     *ngFor="let event of getEventsForDate(day)"
                     [style.background-color]="event.color"
                     [title]="event.title + ' - ' + event.description">
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Loading state for calendar -->
        <div class="loading-state" *ngIf="loading">
          <div class="spinner"></div>
          <p>Loading scheduled maintenances...</p>
        </div>
      </div>
  
      <!-- Schedule Maintenance Form -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">Schedule New Maintenance</h2>
          <p class="section-description">Create a new maintenance window for system updates</p>
        </div>
  
        <form [formGroup]="maintenanceForm" (ngSubmit)="onSubmit()" class="maintenance-form">
          <!-- Maintenance Name -->
          <div class="form-group">
            <label for="name" class="form-label">Maintenance Title *</label>
            <input 
              type="text" 
              id="name"
              formControlName="name"
              class="form-input"
              placeholder="e.g., Database System Upgrade"
              [class.error]="maintenanceForm.get('name')?.invalid && maintenanceForm.get('name')?.touched">
            <div class="error-message" *ngIf="maintenanceForm.get('name')?.invalid && maintenanceForm.get('name')?.touched">
              <span *ngIf="maintenanceForm.get('name')?.errors?.['required']">Maintenance title is required</span>
              <span *ngIf="maintenanceForm.get('name')?.errors?.['minlength']">Title must be at least 3 characters</span>
            </div>
          </div>
  
          <!-- Description -->
          <div class="form-group">
            <label for="description" class="form-label">Description *</label>
            <textarea 
              id="description"
              formControlName="description"
              class="form-textarea"
              rows="4"
              placeholder="Describe the maintenance work, expected impact, and any important details..."
              [class.error]="maintenanceForm.get('description')?.invalid && maintenanceForm.get('description')?.touched">
            </textarea>
            <div class="error-message" *ngIf="maintenanceForm.get('description')?.invalid && maintenanceForm.get('description')?.touched">
              <span *ngIf="maintenanceForm.get('description')?.errors?.['required']">Description is required</span>
              <span *ngIf="maintenanceForm.get('description')?.errors?.['minlength']">Description must be at least 10 characters</span>
            </div>
          </div>
  
          <!-- Date and Time Row -->
          <div class="form-row">
            <div class="form-group">
              <label for="scheduledFor" class="form-label">Scheduled Time (UTC) *</label>
              <input 
                type="datetime-local" 
                id="scheduledFor"
                formControlName="scheduledFor"
                class="form-input"
                [class.error]="maintenanceForm.get('scheduledFor')?.invalid && maintenanceForm.get('scheduledFor')?.touched">
              <div class="help-text">Select the start date and time for the maintenance window</div>
              <div class="error-message" *ngIf="maintenanceForm.get('scheduledFor')?.invalid && maintenanceForm.get('scheduledFor')?.touched">
                Start time is required
              </div>
            </div>
  
            <div class="form-group">
              <label class="form-label">Duration *</label>
              <div class="duration-inputs">
                <div class="duration-field">
                  <input 
                    type="number" 
                    formControlName="durationHours"
                    class="form-input duration-input"
                    min="0"
                    max="72"
                    placeholder="0"
                    [class.error]="maintenanceForm.get('durationHours')?.invalid && maintenanceForm.get('durationHours')?.touched">
                  <span class="duration-label">hours</span>
                </div>
                <div class="duration-field">
                  <input 
                    type="number" 
                    formControlName="durationMinutes"
                    class="form-input duration-input"
                    min="0"
                    max="59"
                    placeholder="0"
                    [class.error]="maintenanceForm.get('durationMinutes')?.invalid && maintenanceForm.get('durationMinutes')?.touched">
                  <span class="duration-label">minutes</span>
                </div>
              </div>
              <div class="help-text">How long will the maintenance take?</div>
              <div class="error-message" *ngIf="(maintenanceForm.get('durationHours')?.invalid || maintenanceForm.get('durationMinutes')?.invalid) && (maintenanceForm.get('durationHours')?.touched || maintenanceForm.get('durationMinutes')?.touched)">
                <span *ngIf="maintenanceForm.get('durationHours')?.errors?.['required'] || maintenanceForm.get('durationMinutes')?.errors?.['required']">Duration is required</span>
                <span *ngIf="getDurationError()">{{ getDurationError() }}</span>
              </div>
            </div>
          </div>
  
          <!-- Impact Level -->
          <div class="form-group">
            <label for="impact" class="form-label">Impact Level *</label>
            <select id="impact" formControlName="impact" class="form-select">
              <option value="minor">Minor - Minimal impact, some services may be slower</option>
              <option value="major">Major - Significant impact, some services unavailable</option>
              <option value="critical">Critical - Full system downtime expected</option>
            </select>
          </div>
  
          <!-- Affected Regions -->
          <div class="form-group">
            <label class="form-label">Affected Regions *</label>
            <div class="checkbox-group-vertical">
              <div class="checkbox-item">
                <input 
                  type="checkbox" 
                  id="mena"
                  (change)="onRegionChange('MENA', $event)"
                  class="form-checkbox">
                <label for="mena" class="checkbox-label">MENA</label>
              </div>
              <div class="checkbox-item">
                <input 
                  type="checkbox" 
                  id="asia"
                  (change)="onRegionChange('ASIA', $event)"
                  class="form-checkbox">
                <label for="asia" class="checkbox-label">ASIA</label>
              </div>
            </div>
            <div class="help-text">Select one or more regions that will be affected by this maintenance</div>
            <div class="error-message" *ngIf="maintenanceForm.get('affectedRegions')?.invalid && maintenanceForm.get('affectedRegions')?.touched">
              <span *ngIf="maintenanceForm.get('affectedRegions')?.errors?.['required']">At least one region must be selected</span>
            </div>
          </div>
  
          <!-- Notification Options -->
          <div class="form-group">
            <div class="checkbox-group">
              <input 
                type="checkbox" 
                id="notifySubscribers"
                formControlName="notifySubscribers"
                class="form-checkbox">
              <label for="notifySubscribers" class="checkbox-label">
                Send notifications to subscribers
                <span class="help-text">Automatically notify all status page subscribers about this maintenance</span>
              </label>
            </div>
          </div>
  
          <!-- Form Actions -->
          <div class="form-actions">
            <button 
              type="button" 
              class="btn btn-secondary"
              (click)="resetForm()">
              Reset Form
            </button>
            <button 
              type="submit" 
              class="btn btn-primary"
              [disabled]="maintenanceForm.invalid || submitting">
              <span *ngIf="submitting" class="button-spinner"></span>
              {{ submitting ? 'Scheduling...' : 'Schedule Maintenance' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  
    <!-- Scheduled Maintenances List -->
    <div class="scheduled-list-section">
      <div class="section-header">
        <h2 class="section-title">Scheduled Maintenances</h2>
        <p class="section-description">Manage upcoming and ongoing maintenance windows</p>
      </div>
  
      <div class="maintenance-list" *ngIf="scheduledMaintenances.length > 0; else noMaintenances">
        <div class="maintenance-card" *ngFor="let maintenance of scheduledMaintenances">
          <div class="maintenance-header">
            <div class="maintenance-title">
              <h3>{{ maintenance.name }}</h3>
              <span class="status-badge" [class]="'status-' + maintenance.status">
                {{ maintenance.status | titlecase }}
              </span>
            </div>
            <div class="maintenance-actions">
              <button type="button" class="btn btn-danger btn-sm" (click)="deleteMaintenance(maintenance.id)">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 2L10 2M2 4H14M12.5 4L12 13C12 13.5 11.5 14 11 14H5C4.5 14 4 13.5 4 13L3.5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
          
          <div class="maintenance-details">
            <div class="maintenance-time">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="icon">
                <path d="M8 1C4.1 1 1 4.1 1 8S4.1 15 8 15 15 11.9 15 8 11.9 1 8 1ZM8 13C5.2 13 3 10.8 3 8S5.2 3 8 3 13 5.2 13 8 10.8 13 8 13Z" fill="currentColor"/>
                <path d="M8 4V8L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ formatDate(maintenance.scheduled_for) }} - {{ formatDate(maintenance.scheduled_until) }}</span>
            </div>
            
            <div class="maintenance-description">
              {{ maintenance.incident_updates?.[0]?.body || 'No description provided' }}
            </div>
            
            <div class="maintenance-regions" *ngIf="maintenance.regions && maintenance.regions.length > 0">
              <span class="regions-label">Affected regions:</span>
              <span class="region-tag" *ngFor="let region of maintenance.regions">
                {{ region }}
              </span>
            </div>
          </div>
        </div>
      </div>
  
      <ng-template #noMaintenances>
        <div class="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="30" stroke="#DFE1E6" stroke-width="2" fill="none"/>
            <path d="M22 32L28 38L42 24" stroke="#36B37E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h3>No Scheduled Maintenances</h3>
          <p>All systems are operating normally. Use the form above to schedule new maintenance windows.</p>
        </div>
      </ng-template>
    </div>
  </div>
  
  <!-- Access Denied Message -->
  <div class="access-denied" *ngIf="!isAdmin">
    <div class="access-denied-content">
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="32" r="30" stroke="#DE350B" stroke-width="2" fill="none"/>
        <path d="M22 22L42 42M22 42L42 22" stroke="#DE350B" stroke-width="3" stroke-linecap="round"/>
      </svg>
      <h2>Access Denied</h2>
      <p>You need administrator privileges to access the maintenance scheduling page.</p>
    </div>
  </div> 